<template>
  <div class="attachment-overlay" tabindex="0" @keydown.esc.stop="onEsc">
    <div
      class="attachment-container"
      :class="{ icon: 'icon' }"
      @click.self.prevent="onClickBackground"
    >
      <embeded-youtube
        v-if="embededUrl"
        :url="embededUrl"
        :autoplay="false"
        :show-controls="true"
      />
      <img v-else-if="imageUrl" :src="imageUrl" />
      <div v-else-if="icon" class="icon-container">
        <svg-icon :icon="icon" :width="'256'" :height="'256'" />
      </div>
      <div v-if="!embededUrl">
        <div class="details">
          <p>
            <span class="name">
              {{ assetName }}
            </span>
            <span v-if="imageUrl">
              -
              {{ assetSize }}
              -
              <a download class="download" :href="imageUrl"> Download </a>
            </span>
            <span v-else-if="icon">
              by
              <a :href="artistPage" target="_blank">
                {{ artist }}
              </a>
              under
              <a
                href="https://creativecommons.org/licenses/by/3.0"
                target="_blank"
              >
                CC BY 3.0
              </a>
            </span>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash/fp';
import { mapGetters, mapActions } from 'vuex';
import SvgIcon from '../all/SvgIcon.vue';
import { EVENT } from '../../constants.js';
import iconMetadata from '../../vuex/icon-metadata.js';
import { has, getYoutubeThumbnail, getYoutubeOEmbed } from '../../utils.js';
import EmbededYoutube from '../all/EmbededYoutube.vue';

export default {
  components: {
    SvgIcon,
    EmbededYoutube,
  },

  data() {
    return {
      changedName: null,
    };
  },

  computed: {
    ...mapGetters('project/data/components', [
      'getNode',
      'getCoverIcon',
      'getDefaultIcon',
      'getContainedNodes',
      'getAssetName',
      'getAssetSize',
      'getAssetExtension',
    ]),
    ...mapGetters('project/config/components', ['getOpen']),
    ...mapGetters('application/appStates', ['getIsProjectLoaded']),
    ...mapGetters('project/data/assets', ['getFullImageUrl']),
    ...mapGetters('application/modals', ['getAssetModal']),

    /**
     * Returns the full image url
     * @return {String|null}
     */
    imageUrl() {
      if (!this.getAssetModal) return null;
      const { type, file, id } = this.getAssetModal;
      return this.getFullImageUrl(id);
    },

    embededUrl() {
      if (!this.getAssetModal) return null;
      const { type, url } = this.getAssetModal;
      if (type === 'youtube') {
        return url;
      }
      return false;
    },

    /**
     * Returns the asset icon (or the default icon)
     * @return {String|null}
     */
    icon() {
      if (!this.getAssetModal) return null;
      const { type, file, id } = this.getAssetModal;
      if (type !== 'icon') return null;
      return file || this.getDefaultIcon;
    },

    /**
     * Returns the artist of the cover icon
     * @return {String}
     */
    artist() {
      if (!this.icon) return 'dummy artist';
      return iconMetadata.getIconArtist(this.icon);
    },

    /**
     * Returns the url of the icon artist page
     * @return {String}
     */
    artistPage() {
      if (!this.icon) return 'dummy artist page';
      return iconMetadata.getIconArtistPage(this.icon);
    },

    /**
     * Returns the name of the cover asset
     * @return {String}
     */
    assetName() {
      return `${this.getAssetName(this.getAssetModal)}.${this.getAssetExtension(
        this.getAssetModal
      )}`;
    },

    /**
     * Returns the size of the cover asset
     * @return {String}
     */
    assetSize() {
      return this.getAssetSize(this.getAssetModal);
    },
  },

  /**
   * Vue mounted lifecycle hook
   */
  mounted() {
    // Give focus to the focusable main div so that it may receive keydown events
    this.$el.focus();
  },

  methods: {
    ...mapActions('project/data/components', ['customUpdate']),
    ...mapActions('project/config/components', ['setOpen']),
    ...mapActions('application/modals', ['setAssetModal']),

    /**
     * Event handler for clicking on modal's background overlay
     */
    onClickBackground() {
      this.setAssetModal({ value: null });
    },

    /**
     * Event handler for pressing the esc key on modal
     */
    onEsc() {
      this.setAssetModal({ value: null });
    },
  },
};
</script>

<style scoped lang="scss">
.attachment-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 100%;
  overflow: auto;
  z-index: 101;
  background: rgba(0, 0, 0, 0.5);
  /* display:flex; */
  /* align-items:center; */
  /* justify-content: center; */
  // cursor:pointer;

  .attachment-container {
    padding: 60px 60px 100px 60px;
    min-height: 100%;
    @media screen and (max-width: 420px) {
      padding: 20px;
    }

    &.icon {
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .icon-container {
      border: 1px solid #191d1e;
      max-width: 100%;
      background: #000;
      border-radius: 12px;
      margin: 0 auto 30px auto;
      .svg-icon-container {
        padding: 70px;
        // border: 4px solid rgba(255, 255, 255, 0.05);
      }
    }

    img {
      /* flex:0; */
      display: block;
      margin: 0 auto;
      border-radius: 6px;
      max-width: 100%;
      /* margin-bottom:60px; */
      cursor: default;
    }

    .details {
      position: fixed;
      left: 0;
      bottom: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      background: rgba(0, 0, 0, 0.75);
      padding: 0 20px;

      p {
        font-size: 14px;
        color: #bcbcbc;

        .name {
          // font-weight: 600;
          color: #fff;
        }

        a {
          color: #db841e;
          text-decoration: none;
          &:hover {
            text-decoration: underline;
          }
        }
      }
    }
  }
}
</style>
<style lang="scss">
.attachment-container {
  svg {
    filter: drop-shadow(0px 0px 30px rgba(0, 0, 0, 1));
  }
}
</style>
